import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft, FileText, MapPin, CheckCircle2, AlertCircle, Briefcase, ShoppingBag, User, Receipt } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function PackingSlip() {
    const { id } = useParams();
    const location = useLocation();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    // Best-effort back path based on the route the user came from
    const inAdmin = location.pathname.startsWith("/admin");
    const backTo = inAdmin ? "/admin/packing-slips" : "/employee/packing";

    // Who can create invoices? Admin / Editor / Accounts & Billing employee.
    const canInvoice = user && (
        user.role === "admin"
        || user.role === "editor"
        || (user.role === "employee" && (user.designation_name === "Accounts & Billing" || (user.capabilities || []).includes("accounts")))
    );
    const invoicePath = inAdmin ? "/admin/invoices/new" : "/employee/accounts/invoices/new";

    useEffect(() => {
        api.get(`/packing/slips/${id}`)
            .then(r => setData(r.data))
            .catch(e => {
                const msg = e.response?.data?.detail || "Couldn't load slip";
                setErr(msg);
                toast.error(msg);
            });
    }, [id]);

    if (err) return (
        <div className="max-w-3xl mx-auto p-8 text-center bg-white border border-red-200 mt-12">
            <AlertCircle className="w-8 h-8 mx-auto text-red-500 mb-2" />
            <p className="text-sm text-red-800">{err}</p>
            <Link to={backTo} className="text-sky-700 hover:underline text-sm mt-3 inline-block">Back</Link>
        </div>
    );
    if (!data) return <div className="p-8 text-center text-sm text-stone-500">Loading slip…</div>;

    const o = data.order;
    const packing = o.packing || {};
    const packedItems = packing.items || [];
    const items = o.items || [];
    const oos = packedItems.filter(p => !p.available);
    const packed = packedItems.filter(p => p.available && (p.packed_quantity || 0) > 0);
    const totalOrdered = items.reduce((s, it) => s + (it.quantity || 0), 0);
    const totalPacked = packedItems.reduce((s, p) => s + (p.available ? (p.packed_quantity || 0) : 0), 0);
    const slipNo = `PS-${o.id.slice(0, 8).toUpperCase()}`;
    const oboName = o.on_behalf_of?.name;
    const isB2B = data.is_b2b;

    return (
        <div className="bg-stone-100 min-h-screen py-6 print:bg-white print:py-0" data-testid="packing-slip-page">
            <Toaster position="top-center" />

            {/* Action bar — hidden on print */}
            <div className="max-w-4xl mx-auto px-4 mb-4 flex items-center justify-between print:hidden">
                <Link to={backTo} className="text-sm text-sky-700 hover:underline inline-flex items-center gap-1" data-testid="slip-back">
                    <ArrowLeft className="w-4 h-4" />Back
                </Link>
                <div className="flex items-center gap-2">
                    {canInvoice && (o.billing_status === "pending" || !o.invoice_id) && (
                        <Link
                            to={`${invoicePath}?order_id=${o.id}`}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-stone-50 text-sm border border-emerald-700"
                            data-testid="slip-generate-invoice"
                        >
                            <Receipt className="w-4 h-4" />Generate invoice
                        </Link>
                    )}
                    {canInvoice && o.invoice_id && (
                        <Link
                            to={inAdmin ? `/admin/invoices/${o.invoice_id}` : `/employee/accounts/invoices/${o.invoice_id}`}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-800 text-sm border border-stone-300"
                            data-testid="slip-open-invoice"
                        >
                            <Receipt className="w-4 h-4" />Open invoice {data.invoice_number ? `· ${data.invoice_number}` : ""}
                        </Link>
                    )}
                    <Button onClick={() => window.print()} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="slip-print">
                        <Printer className="w-4 h-4 mr-2" />Print slip
                    </Button>
                </div>
            </div>

            {/* Slip body */}
            <div className="max-w-4xl mx-auto bg-white border border-stone-200 p-8 print:border-0 print:max-w-none print:p-6">
                {/* Header */}
                <div className="flex items-start justify-between border-b-2 border-stone-900 pb-4 mb-4">
                    <div>
                        <div className="font-serif text-2xl text-stone-900 font-bold">{data.company.name}</div>
                        {data.company.address && <div className="text-xs text-stone-600 mt-1 whitespace-pre-line">{data.company.address}</div>}
                        <div className="text-xs text-stone-500 mt-1">
                            {[data.company.phone, data.company.email].filter(Boolean).join(" · ")}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="label-eyebrow text-sky-700">Packing Slip</div>
                        <div className="font-serif text-2xl text-stone-900 mt-1 font-bold">{slipNo}</div>
                        <div className="text-xs text-stone-500 mt-1">Order #{o.id.slice(0, 8).toUpperCase()}</div>
                        {data.invoice_number && (
                            <div className="text-xs text-stone-600 mt-0.5">Invoice: <span className="font-mono">{data.invoice_number}</span></div>
                        )}
                    </div>
                </div>

                {/* Meta + Type badge */}
                <div className="grid sm:grid-cols-3 gap-4 mb-5 text-xs">
                    <div>
                        <div className="label-eyebrow text-stone-500 mb-1 flex items-center gap-1">
                            {isB2B ? <Briefcase className="w-3 h-3" /> : <ShoppingBag className="w-3 h-3" />}
                            Order type
                        </div>
                        <div className={`inline-block uppercase tracking-widest text-[10px] font-semibold px-2 py-1 ${isB2B ? "bg-indigo-100 text-indigo-700" : "bg-sky-100 text-sky-700"}`}>
                            {o.order_type} {isB2B ? "(B2B)" : "(Retail)"}
                        </div>
                    </div>
                    <div>
                        <div className="label-eyebrow text-stone-500 mb-1">Placed on</div>
                        <div className="text-stone-900">{o.created_at?.slice(0, 16).replace("T", " ")}</div>
                    </div>
                    <div>
                        <div className="label-eyebrow text-stone-500 mb-1">Packed on</div>
                        <div className="text-stone-900">{packing.packed_at?.slice(0, 16).replace("T", " ") || "—"}</div>
                        <div className="text-[10px] text-stone-500">by {packing.packed_by_name || "—"}</div>
                    </div>
                </div>

                {/* Customer + Ship to */}
                <div className="grid sm:grid-cols-2 gap-4 mb-5">
                    <div className="border border-stone-200 p-3 text-xs">
                        <div className="label-eyebrow text-stone-500 mb-2 flex items-center gap-1"><User className="w-3 h-3" />Customer</div>
                        <div className="font-medium text-stone-900 text-sm">{oboName || o.user_name}</div>
                        {o.on_behalf_of?.customer_type && o.on_behalf_of.customer_type !== "retailer" && (
                            <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5">{o.on_behalf_of.customer_type}</div>
                        )}
                        {o.on_behalf_of?.phone && <div className="text-stone-600">{o.on_behalf_of.phone}</div>}
                        {oboName && o.user_name && o.user_name !== oboName && (
                            <div className="text-[10px] text-stone-500 mt-1">Placed by: {o.user_name} ({o.user_role})</div>
                        )}
                    </div>
                    <div className="border border-stone-200 p-3 text-xs">
                        <div className="label-eyebrow text-stone-500 mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" />Ship to</div>
                        <div className="font-medium text-stone-900 text-sm">{o.address?.name}</div>
                        {o.address?.phone && <div className="text-stone-600">{o.address.phone}</div>}
                        {(o.address?.line1 || o.address?.line2) && <div className="text-stone-600">{[o.address?.line1, o.address?.line2].filter(Boolean).join(", ")}</div>}
                        <div className="text-stone-600">{[o.address?.city, o.address?.state, o.address?.pincode].filter(Boolean).join(", ")}</div>
                    </div>
                </div>

                {/* Items table */}
                <table className="w-full text-sm border-t border-b border-stone-300 mb-4">
                    <thead>
                        <tr className="border-b border-stone-300 bg-stone-50">
                            <th className="text-left p-2 text-[10px] uppercase tracking-widest text-stone-500">#</th>
                            <th className="text-left p-2 text-[10px] uppercase tracking-widest text-stone-500">Product</th>
                            <th className="text-left p-2 text-[10px] uppercase tracking-widest text-stone-500">Size</th>
                            <th className="text-right p-2 text-[10px] uppercase tracking-widest text-stone-500">Ordered</th>
                            <th className="text-right p-2 text-[10px] uppercase tracking-widest text-stone-500">Packed</th>
                            <th className="text-center p-2 text-[10px] uppercase tracking-widest text-stone-500">Status</th>
                            <th className="text-left p-2 text-[10px] uppercase tracking-widest text-stone-500">Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it, idx) => {
                            const pi = packedItems.find(p => p.idx === idx) || { packed_quantity: 0, available: false };
                            return (
                                <tr key={idx} className={`border-b border-stone-100 ${pi.available ? "" : "bg-red-50/30"}`} data-testid={`slip-line-${idx}`}>
                                    <td className="p-2 font-mono text-xs text-stone-500">{idx + 1}</td>
                                    <td className={`p-2 ${pi.available ? "" : "line-through text-stone-500"}`}>{it.product_name}</td>
                                    <td className="p-2 text-xs text-stone-600">{it.size_label}</td>
                                    <td className="p-2 text-right font-mono">{it.quantity}</td>
                                    <td className="p-2 text-right font-mono font-semibold">{pi.available ? pi.packed_quantity : 0}</td>
                                    <td className="p-2 text-center">
                                        {pi.available
                                            ? <span className="inline-flex items-center gap-1 text-emerald-700 text-[11px]"><CheckCircle2 className="w-3 h-3" />Packed</span>
                                            : <span className="inline-flex items-center gap-1 text-red-700 text-[11px] font-semibold"><AlertCircle className="w-3 h-3" />OOS</span>}
                                    </td>
                                    <td className="p-2 text-xs text-stone-600">{pi.note || ""}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {/* Summary */}
                <div className="grid sm:grid-cols-3 gap-3 mb-6">
                    <div className="bg-stone-50 border border-stone-200 px-3 py-2 text-xs">
                        <div className="label-eyebrow text-stone-500">Lines</div>
                        <div className="font-serif text-xl text-stone-900">{items.length}</div>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs">
                        <div className="label-eyebrow text-emerald-700">Packed / Ordered</div>
                        <div className="font-serif text-xl text-emerald-800">{totalPacked} <span className="text-stone-400">/ {totalOrdered}</span></div>
                        <div className="text-[10px] text-emerald-700">{packed.length} line{packed.length === 1 ? "" : "s"} dispatched</div>
                    </div>
                    <div className={`border px-3 py-2 text-xs ${oos.length > 0 ? "bg-red-50 border-red-200" : "bg-stone-50 border-stone-200"}`}>
                        <div className={`label-eyebrow ${oos.length > 0 ? "text-red-700" : "text-stone-500"}`}>Out of stock</div>
                        <div className={`font-serif text-xl ${oos.length > 0 ? "text-red-800" : "text-stone-900"}`}>{oos.length}</div>
                        <div className="text-[10px] text-stone-500">line{oos.length === 1 ? "" : "s"} not dispatched</div>
                    </div>
                </div>

                {/* OOS detail box (only if any) */}
                {oos.length > 0 && (
                    <div className="border-2 border-red-200 bg-red-50/50 p-3 mb-6">
                        <div className="text-xs font-semibold text-red-800 flex items-center gap-1 mb-2"><AlertCircle className="w-3 h-3" />Items not dispatched (out of stock)</div>
                        <ul className="text-xs text-red-900 space-y-1 list-disc pl-5">
                            {oos.map((p, i) => {
                                const it = items[p.idx];
                                if (!it) return null;
                                return <li key={i}>{it.product_name} · {it.size_label} (ordered {it.quantity}){p.note ? ` — note: ${p.note}` : ""}</li>;
                            })}
                        </ul>
                    </div>
                )}

                {/* Totals + sign-off */}
                <div className="grid sm:grid-cols-2 gap-6 mb-2 border-t-2 border-stone-300 pt-4">
                    <div className="text-xs space-y-1 text-stone-600">
                        <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{formatINR(o.subtotal || 0)}</span></div>
                        <div className="flex justify-between"><span>Shipping</span><span className="font-mono">{formatINR(o.shipping || 0)}</span></div>
                        <div className="flex justify-between border-t border-stone-300 pt-1 text-sm font-semibold text-stone-900"><span>Total</span><span className="font-mono">{formatINR(o.total || 0)}</span></div>
                        <div className="flex justify-between text-[10px] text-stone-500 pt-1">
                            <span>Payment</span><span className="uppercase">{o.payment_method} · {o.payment_status}</span>
                        </div>
                    </div>
                    <div className="text-xs">
                        <div className="border-t border-stone-300 pt-2 mt-12">
                            <div className="text-stone-500">Packer signature</div>
                            <div className="text-stone-700 mt-1">{packing.packed_by_name || "—"}</div>
                        </div>
                        <div className="border-t border-stone-300 pt-2 mt-8">
                            <div className="text-stone-500">Receiver signature</div>
                        </div>
                    </div>
                </div>

                <div className="text-[10px] text-stone-400 text-center mt-6">
                    Generated electronically · {data.company.name} · Slip {slipNo}
                </div>
            </div>

            {/* Print CSS */}
            <style>{`
                @media print {
                    @page { size: A4; margin: 12mm; }
                    body { background: #fff !important; }
                }
            `}</style>
        </div>
    );
}
